/**
 * PageHeader — title row above the editor.
 *
 * Phase 3 surfaces:
 *   - Breadcrumb of ancestors (root → page).
 *   - Click-to-edit title (Notion-style: textarea that auto-grows so
 *     long titles wrap to a second line instead of overflowing into the
 *     page details sidebar). Enter / blur commits; Escape reverts.
 *   - Click-to-change icon — opens the EmojiPicker popover. The chosen
 *     emoji is PATCHed via `useUpdatePage` and the optimistic update
 *     lands immediately. The kebab menu (archive / restore / delete)
 *     ships in Phase 4.
 *
 * The icon trigger is a real `<button>` (keyboard-reachable, focus ring
 * intact) so the affordance is discoverable; hover hints via
 * `hover:bg-surface-100`.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { Page } from '@friday/shared/realtime'
import { useUpdatePage } from '@/features/notebook/pages'
import { EmojiPicker } from './EmojiPicker'

export interface PageHeaderProps {
  wsId: string
  page: Page
  /** Breadcrumb ancestors (root first). */
  ancestors: Page[]
  onOpenParent: (pageId: string) => void
}

export function PageHeader({ wsId, page, ancestors, onOpenParent }: PageHeaderProps) {
  const [title, setTitle] = useState(page.title)
  const [pickerOpen, setPickerOpen] = useState(false)
  const iconButtonRef = useRef<HTMLButtonElement | null>(null)
  const inputRef = useRef<HTMLTextAreaElement | null>(null)
  const update = useUpdatePage(wsId)

  // Keep local title in sync when navigating between pages.
  useEffect(() => {
    setTitle(page.title)
  }, [page.id, page.title])

  // Auto-grow the title textarea to fit its content (Notion-style — long
  // titles wrap to a second line instead of being truncated with an
  // ellipsis). The single-line baseline comes from `rows={1}`; we measure
  // `scrollHeight` after each render and resize up to a 6-line cap so a
  // pathological paste doesn't blow up the page.
  useLayoutEffect(() => {
    const el = inputRef.current
    if (!el) return
    el.style.height = 'auto'
    const lineHeight = 36 // matches text-2xl leading at this font size
    const maxHeight = lineHeight * 6
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`
  }, [title])

  const commitTitle = () => {
    // Strip newlines on commit — a wrapped title is for display only.
    // The user can keep the visual wrap by letting the textarea grow
    // (it never inserts a newline on Enter; that's intercepted below).
    const next = title.replace(/\s+/g, ' ').trim() || 'Untitled'
    if (next !== page.title) {
      update.mutate({ pageId: page.id, version: page.version, patch: { title: next } })
    }
  }

  const commitIcon = (icon: string) => {
    if (icon === (page.icon ?? '📄')) return
    update.mutate({ pageId: page.id, version: page.version, patch: { icon } })
  }

  return (
    <div className="border-b border-surface-200 px-6 py-4 md:pr-14 dark:border-surface-800">
      <nav className="mb-2 flex flex-wrap items-center gap-1 text-xs text-surface-500">
        {ancestors.map((a) => (
          <span key={a.id} className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onOpenParent(a.id)}
              className="rounded px-1 hover:bg-surface-100 hover:text-brand-600 dark:hover:bg-surface-800 dark:hover:text-brand-300"
            >
              {a.icon ?? '📄'} {a.title}
            </button>
            <span className="text-surface-400">/</span>
          </span>
        ))}
        {ancestors.length === 0 && <span className="text-surface-500">Workspace root</span>}
      </nav>
      <div className="flex items-start gap-2">
        <button
          ref={iconButtonRef}
          type="button"
          onClick={() => setPickerOpen(true)}
          aria-label="Change page icon"
          title="Change icon"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded text-2xl transition-colors hover:bg-surface-100 focus:bg-surface-100 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:hover:bg-surface-800 dark:focus:bg-surface-800"
        >
          {page.icon ?? '📄'}
        </button>
        {/*
          Notion-style title: a single-line textarea that auto-grows to
          wrap long titles. `Enter` commits and blurs (so the title stays
          a single string), `Shift+Enter` inserts a newline (escape hatch
          for multi-line titles), `Escape` reverts and blurs.
        */}
        <textarea
          ref={inputRef}
          value={title}
          rows={1}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={commitTitle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              ;(e.target as HTMLTextAreaElement).blur()
            } else if (e.key === 'Escape') {
              setTitle(page.title)
              ;(e.target as HTMLTextAreaElement).blur()
            }
          }}
          className="min-h-9 w-full resize-none border-0 bg-transparent text-2xl font-semibold leading-9 text-app outline-none placeholder:text-surface-400 focus:ring-0"
          placeholder="Untitled"
          aria-label="Page title"
          style={{ height: '36px' }}
        />
      </div>
      {pickerOpen && iconButtonRef.current && (
        <EmojiPicker
          value={page.icon ?? '📄'}
          onChange={commitIcon}
          onClose={() => setPickerOpen(false)}
          anchorEl={iconButtonRef.current}
        />
      )}
    </div>
  )
}
